import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDownLeft, ArrowUpRight, Lock, Scale, Wallet } from 'lucide-react'
import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { ZERO_CASH, normalizeTerminalId, parseCashAmount } from '@aeris/cash'
import type { CashMovementSummary } from '@aeris/contracts'
import {
  MONEY_SCALE,
  formatMoney,
  parseHumanAmount,
  toString,
} from '@aeris/money'
import {
  Badge,
  Button,
  EmptyState,
  Field,
  InlineAlert,
  LoadFailed,
  MoneyInput,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  SortableHeaderCell,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  useScopedKeydown,
} from '@aeris/ui'

import { CashCloseForm } from '../components/cash-close-form.tsx'
import { CashHistory } from '../components/cash-history.tsx'
import {
  CashMovementForm,
  type CashOperation,
} from '../components/cash-movement-form.tsx'
import { PixTodayLine } from '../components/pix-today-line.tsx'
import { SalesDocumentDialog } from '../components/sales-document-dialog.tsx'
import { TerminalSelect } from '../components/terminal-select.tsx'
import {
  ApiError,
  fetchCashMovements,
  fetchCurrentCashSession,
  openCashSession,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { CASH_KIND_LABELS } from '../lib/cash-labels.ts'
import {
  readRememberedTerminal,
  rememberTerminal,
} from '../lib/remembered-terminal.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'
import { readEdgeAnswer } from '../lib/use-till-backend.ts'

/**
 * O caixa — the till session (Master Specification section 18).
 *
 * Keyboard-first, as section 18 demands: **an operator's hands should not have
 * to leave the keyboard.** F2 opens a sangria, F3 a suprimento, F4 the
 * fechamento; every dialog focuses its amount field on open, Enter submits and
 * Esc cancels. The buttons exist for discoverability and for a mouse, but the
 * shortcut is the primary path and the labels say so.
 *
 * What this screen is NOT is the selling screen. A sale needs the sales domain
 * and payment methods, both of which are separate features; this is the session
 * underneath them, which is a complete and useful thing on its own — a shop
 * opens, supplies and bleeds a till whether or not anything was sold.
 *
 * No figure here is editable except the two a person genuinely knows: the fundo
 * de troco at opening, and what they counted at closing. The expected balance is
 * derived from the movements, every time, by the server.
 */

const KIND_LABELS = CASH_KIND_LABELS

/** Amounts are formatted from the exact decimal, never from a `Number()`. */
const asMoney = (value: string): string =>
  formatMoney(parseCashAmount(value) ?? ZERO_CASH)

const timeOf = (iso: string): string =>
  new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })

export const CaixaPage = (): ReactNode => {
  const { allows, session: authSession } = useAuth()
  const queryClient = useQueryClient()

  const [terminalId, setTerminalId] = useState(readRememberedTerminal)
  const hasTerminal = terminalId.trim().length > 0
  const [openingFloat, setOpeningFloat] = useState('')
  const [operation, setOperation] = useState<CashOperation | null>(null)
  const [closing, setClosing] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const canSupervise = allows('pos.supervise')
  const branchId = authSession?.context?.branchId ?? undefined

  // A memory of the last choice, not the source of the value: TerminalSelect
  // opens empty if what is stored here is no longer in the register.
  useEffect(() => {
    rememberTerminal(terminalId)
  }, [terminalId])

  const current = useQuery({
    queryKey: ['cash-session', { terminalId, branchId }],
    queryFn: ({ signal }) =>
      fetchCurrentCashSession(
        {
          terminalId: normalizeTerminalId(terminalId),
          ...(branchId ? { branchId } : {}),
        },
        signal
      ),
    // Nothing to ask about until a till is chosen.
    enabled: hasTerminal,
  })

  const session = current.data?.session ?? null

  const sort = useTableSort<'occurredAt' | 'amount'>('occurredAt', 'desc')

  /*
   * Paginado (F132): a lista pedia 100 e contava as linhas que vieram, então um
   * caixa de sábado com 140 movimentos dizia "100 movimentos" e escondia 40.
   */
  const [movementsPage, setMovementsPage] = useState(1)
  const movements = useQuery({
    queryKey: ['cash-movements', session?.id, sortKey(sort), movementsPage],
    queryFn: ({ signal }) =>
      fetchCashMovements(session?.id ?? '', signal, {
        sort: sort.sort,
        direction: sort.direction,
        page: movementsPage,
      }),
    enabled: session !== null,
  })

  const typedFloat =
    openingFloat.trim().length === 0
      ? null
      : parseHumanAmount(openingFloat, MONEY_SCALE)

  const openMutation = useMutation({
    mutationFn: async () =>
      await openCashSession({
        terminalId: normalizeTerminalId(terminalId),
        ...(branchId ? { branchId } : {}),
        // Zero is legitimate: a till can open with an empty drawer.
        openingFloat: toString(typedFloat ?? ZERO_CASH),
        notes: null,
      }),
    onSuccess: async () => {
      setOpeningFloat('')
      await queryClient.invalidateQueries({ queryKey: ['cash-session'] })
      await queryClient.invalidateQueries({ queryKey: ['cash-sessions'] })
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível abrir o caixa. Tente novamente.'
      )
    },
  })

  /**
   * The shortcuts section 18 asks for.
   *
   * Bound on the window rather than on a container, because the operator's
   * focus is wherever the last action left it and a shortcut that only works
   * when the right element is focused is a shortcut nobody trusts. Suppressed
   * while a dialog is open so F4 cannot start a fechamento on top of a sangria.
   */
  const dialogOpen = operation !== null || closing

  /*
   * Vendas deste computador que ainda não subiram (F197).
   *
   * Fechar o caixa na nuvem com vendas presas no Edge produz um fechado
   * errado: o esperado não conta o que foi vendido sem internet, e a
   * diferença aparece como falta de dinheiro. Enquanto houver pendente, o
   * fechamento espera — e a tela diz por quê.
   */
  const edge = useQuery({
    queryKey: ['edge', 'sync-status'],
    queryFn: async ({ signal }) => await readEdgeAnswer(signal),
    refetchInterval: 30_000,
    retry: false,
    throwOnError: false,
  })
  const edgePending =
    edge.data?.state === 'paired' ? edge.data.status.pending : 0
  const closeBlocked = edgePending > 0

  // `useScopedKeydown` (F138): no Modo PC o Caixa divide a página com outras
  // janelas, e o F2 do PDV ao lado não pode abrir uma sangria aqui.
  useScopedKeydown(
    (event: KeyboardEvent): void => {
      if (event.key === 'F2') {
        event.preventDefault()
        setOperation('withdrawal')
      }
      if (event.key === 'F3') {
        event.preventDefault()
        setOperation('deposit')
      }
      if (event.key === 'F4') {
        event.preventDefault()
        if (!closeBlocked) setClosing(true)
      }
    },
    session !== null && !dialogOpen
  )

  const rows: CashMovementSummary[] = useMemo(
    () => movements.data?.items ?? [],
    [movements.data]
  )

  /** As vendas que entraram em dinheiro nesta página, da mais antiga (F286). */
  const cashSales = [
    ...new Map(
      rows
        .filter((row) => row.direction === 'in' && row.documentNumber !== null)
        .map((row) => [
          row.documentNumber ?? '',
          { number: row.documentNumber ?? '', id: row.documentId },
        ])
    ).values(),
  ].sort((left, right) => Number(left.number) - Number(right.number))

  /*
   * Abrir a venda daqui (F298): "dá para abrir a nota caso precisar ver se
   * ela deu baixa errada". Só para quem enxerga vendas.
   */
  const canSeeSale = allows('sales.read')
  const [viewingSale, setViewingSale] = useState<string | null>(null)
  const saleLink = (
    label: string,
    documentId: string | null
  ): ReactNode =>
    documentId === null || !canSeeSale ? (
      label
    ) : (
      <button
        type="button"
        className="font-(--weight-medium) text-(--brand-primary) underline-offset-2 hover:underline"
        onClick={() => {
          setViewingSale(documentId)
        }}
      >
        {label}
      </button>
    )

  // Closing somebody else's till is what `pos.supervise` costs; the API refuses
  // it independently, and this only decides which sentence the dialog shows.
  const supervising =
    session !== null && session.openedByUserId !== authSession?.user.id

  return (
    <Page>
      <PageHeader
        title="Caixa"
        description="O saldo esperado vem dos movimentos. Você informa apenas o que contou."
      />

      <PixTodayLine />

      {formError === null ? null : (
        <InlineAlert tone="danger">{formError}</InlineAlert>
      )}

      {hasTerminal && current.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner label="Carregando o caixa" />
        </div>
      ) : current.isError ? (
        <LoadFailed
          what="o caixa"
          onRetry={() => {
            void current.refetch()
          }}
        />
      ) : session === null ? (
        // ------------------------------------------------------------------
        // Abertura de caixa
        // ------------------------------------------------------------------
        <form
          onSubmit={(event) => {
            event.preventDefault()
            setFormError(null)
            if (openingFloat.trim().length > 0 && typedFloat === null) {
              setFormError('Informe um valor. Use vírgula para os centavos.')
              return
            }
            openMutation.mutate()
          }}
          noValidate
          className="flex flex-col gap-4 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4"
        >
          <div>
            <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
              Abrir o caixa
            </h2>
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              Informe o fundo de troco que está na gaveta. Ele entra no
              histórico como o primeiro movimento.
            </p>
          </div>

          <div className="flex flex-wrap items-start gap-4">
            <TerminalSelect value={terminalId} onValueChange={setTerminalId} />

            <Field label="Fundo de troco" hint="Pode ser zero" className="w-56">
              {(field) => (
                <MoneyInput
                  {...field}
                  value={openingFloat}
                  onValueChange={setOpeningFloat}
                  autoFocus
                />
              )}
            </Field>
          </div>

          <div>
            {/* No till chosen means nothing to open. The server refuses
                it too — this only avoids asking. */}
            <Button
              type="submit"
              disabled={openMutation.isPending || !hasTerminal}
            >
              Abrir caixa
            </Button>
          </div>
        </form>
      ) : (
        // ------------------------------------------------------------------
        // Caixa aberto
        // ------------------------------------------------------------------
        <>
          <section
            aria-label="Situação do caixa"
            className="flex flex-wrap items-end justify-between gap-4 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4"
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="font-(--font-mono) text-(length:--text-sm) text-(--text-secondary)">
                  {session.terminalId}
                </span>
                <Badge tone="success">Aberto</Badge>
              </div>
              <p className="text-(length:--text-xs) text-(--text-muted)">
                Aberto por {session.openedByName ?? '—'} às{' '}
                {timeOf(session.openedAt)} · {session.branchName}
              </p>
            </div>

            {/*
              The number the whole screen exists to keep honest. Announced,
              because it changes without the page reloading.
            */}
            <div className="flex flex-col items-end">
              <span className="text-(length:--text-xs) text-(--text-secondary)">
                Saldo esperado
              </span>
              <output
                aria-live="polite"
                className="font-(--font-mono) text-(length:--text-2xl) font-(--weight-semibold) text-(--text-primary)"
              >
                {asMoney(session.expected)}
              </output>
            </div>
          </section>

          <div className="flex flex-wrap gap-2">
            {/*
              A sangria é de todos (F249): quem não é supervisor registra com a
              senha do gerente, pedida no próprio formulário. O ajuste continua
              escondido de quem não supervisiona — e a API recusa cada pedido
              por conta própria.
            */}
            <Button
              variant="secondary"
              onClick={() => {
                setOperation('withdrawal')
              }}
              leadingIcon={
                <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
              }
            >
              Sangria (F2)
            </Button>

            <Button
              variant="secondary"
              onClick={() => {
                setOperation('deposit')
              }}
              leadingIcon={
                <ArrowDownLeft aria-hidden="true" className="h-4 w-4" />
              }
            >
              Suprimento (F3)
            </Button>

            {canSupervise ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setOperation('adjustment')
                }}
                leadingIcon={<Scale aria-hidden="true" className="h-4 w-4" />}
              >
                Ajustar
              </Button>
            ) : null}

            <Button
              disabled={closeBlocked}
              onClick={() => {
                setClosing(true)
              }}
              leadingIcon={<Lock aria-hidden="true" className="h-4 w-4" />}
            >
              Fechar caixa (F4)
            </Button>
          </div>

          {closeBlocked ? (
            <InlineAlert tone="warning" title="Ainda há vendas sem subir">
              {edgePending === 1
                ? '1 lançamento feito sem internet ainda não chegou à nuvem.'
                : `${String(edgePending)} lançamentos feitos sem internet ainda não chegaram à nuvem.`}{' '}
              Fechar agora daria um fechado errado. O fechamento libera assim
              que eles subirem.
            </InlineAlert>
          ) : null}

          <p
            aria-live="polite"
            className="text-(length:--text-xs) text-(--text-muted)"
          >
            {movements.isPending
              ? 'Carregando movimentos…'
              : movements.isError
                ? 'Os movimentos não carregaram'
                : `${String(movements.data.total)} movimento${movements.data.total === 1 ? '' : 's'} neste caixa`}
          </p>

          {/*
            Falhou não é "Nenhum movimento ainda" (F132). Com a consulta fora,
            a lista vazia afirmava que ninguém tinha feito sangria nem
            suprimento — num caixa que está sendo conferido.
          */}
          <QueryState
            isPending={movements.isPending}
            isError={movements.isError}
            error={movements.error}
            onRetry={() => {
              void movements.refetch()
            }}
            what="os movimentos"
            isEmpty={rows.length === 0}
            empty={
              <EmptyState
                icon={<Wallet aria-hidden="true" className="h-8 w-8" />}
                title="Nenhum movimento ainda"
                description="A abertura sem fundo de troco não gera movimento. Um suprimento ou uma sangria aparece aqui."
              />
            }
          >
            {cashSales.length > 0 && (
              <p className="pb-2 text-(length:--text-sm) text-(--text-secondary)">
                Vendas em dinheiro:{' '}
                <span className="font-(--font-mono) text-(--text-primary)">
                  {cashSales.map((sale, index) => (
                    <Fragment key={sale.number}>
                      {index === 0 ? null : ', '}
                      {saleLink(`#${sale.number}`, sale.id)}
                    </Fragment>
                  ))}
                </span>
              </p>
            )}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableHeaderCell
                      kind="date"
                      {...sort.column('occurredAt')}
                    >
                      Hora
                    </SortableHeaderCell>
                    <TableHeaderCell>Movimento</TableHeaderCell>
                    <TableHeaderCell>Quem</TableHeaderCell>
                    <TableHeaderCell>Motivo</TableHeaderCell>
                    <SortableHeaderCell numeric {...sort.column('amount')}>
                      Valor
                    </SortableHeaderCell>
                    <TableHeaderCell numeric>Saldo</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-(--font-mono) whitespace-nowrap">
                        {timeOf(row.occurredAt)}
                      </TableCell>
                      <TableCell className="text-(--text-primary)">
                        {KIND_LABELS[row.kind]}
                      </TableCell>
                      <TableCell className="text-(--text-secondary)">
                        {row.actorName ?? '—'}
                      </TableCell>
                      <TableCell className="text-(--text-secondary)">
                        {/* Qual venda entrou em dinheiro (F286), e a baixa
                            de título dela (F298) — clicável. */}
                        {row.documentNumber === null ? (
                          (row.note ?? '—')
                        ) : (
                          <>
                            {saleLink(
                              `${row.sourceType === 'finance_settlement' ? 'Baixa da venda' : 'Venda'} #${row.documentNumber}`,
                              row.documentId
                            )}
                            {row.note === null ? '' : ` · ${row.note}`}
                          </>
                        )}
                      </TableCell>
                      <TableCell
                        numeric
                        className={
                          row.direction === 'out'
                            ? 'font-(--font-mono) text-(--danger)'
                            : 'font-(--font-mono) text-(--text-primary)'
                        }
                      >
                        {row.direction === 'out' ? '−' : '+'}
                        {asMoney(row.amount)}
                      </TableCell>
                      <TableCell
                        numeric
                        className="font-(--font-mono) font-(--weight-medium)"
                      >
                        {asMoney(row.balanceAfter)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </QueryState>

          <Pagination
            page={movementsPage}
            pageSize={100}
            total={movements.data?.total ?? 0}
            onPageChange={setMovementsPage}
            label="Páginas de movimentos"
          />
        </>
      )}

      {/* F217: os fechamentos, com ou sem caixa aberto agora. */}
      <CashHistory />

      {/* Mounted only while open and keyed by the operation, so each form
          starts from empty without an effect that resets it. */}
      {operation !== null && session !== null ? (
        <CashMovementForm
          key={`${session.id}-${operation}`}
          operation={operation}
          session={session}
          needsAuthorisation={!canSupervise}
          onClose={() => {
            setOperation(null)
          }}
        />
      ) : null}

      {closing && session !== null ? (
        <CashCloseForm
          key={`${session.id}-close`}
          session={session}
          supervising={supervising}
          onClose={() => {
            setClosing(false)
          }}
        />
      ) : null}

      {viewingSale === null ? null : (
        <SalesDocumentDialog
          documentId={viewingSale}
          onClose={() => {
            setViewingSale(null)
          }}
        />
      )}
    </Page>
  )
}
